import { useEffect, useRef, useState } from 'react';
import {
  isHttpsAddress,
  MAX_FILE_BYTES,
  warmUp,
  type RunLanguage,
  type RunOptions,
  type RunSource,
} from './api';
import { megabytes, type OptionsState } from './CleanTextInput';
import type { RunError, useCleanTextRun } from './use-clean-text-run';

/** Devops' point a: wake the service when the screen opens, without waiting for it to send. */
export function useWarmUp(): 'pending' | 'done' {
  const [warm, setWarm] = useState<'pending' | 'done'>('pending');
  const warmed = useRef(false);
  useEffect(() => {
    if (warmed.current) return;
    warmed.current = true;
    void warmUp().then(() => setWarm('done'));
  }, []);
  return warm;
}

/** Checked before upload (decision 158): nothing is sent. */
const tooLarge = (picked: File): RunError => ({
  where: 'file',
  key: 'tooLarge.local',
  values: { size: megabytes(picked.size), limit: megabytes(MAX_FILE_BYTES) },
});

const sourceOf = (tab: 'file' | 'url', file: File | null, url: string): RunSource | null => {
  if (tab === 'url') return { kind: 'url', url: url.trim() };
  return file ? { kind: 'file', file } : null;
};

export function safeHost(address: string): string {
  try {
    return new URL(address).hostname;
  } catch {
    return address;
  }
}

function useOptionsState() {
  const [language, setLanguage] = useState<RunLanguage>('auto');
  const [extraTerms, setExtraTerms] = useState<readonly string[]>([]);
  const [keepTerms, setKeepTerms] = useState<readonly string[]>([]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const termsInput = useRef<HTMLInputElement | null>(null);
  const focusTerms = useRef(false);
  const options: OptionsState = {
    language,
    extraTerms,
    keepTerms,
    open: optionsOpen,
    termsInput,
    onLanguage: setLanguage,
    onExtraTerms: setExtraTerms,
    onKeepTerms: setKeepTerms,
    onToggle: () => setOptionsOpen((open) => !open),
  };
  return {
    language,
    extraTerms,
    keepTerms,
    setLanguage,
    setOptionsOpen,
    focusTerms,
    termsInput,
    optionsOpen,
    options,
  };
}

/** «Aggiungilo e rielabora» opens the options with the term field ready. */
function useFocusTerms(opts: ReturnType<typeof useOptionsState>, inputPhase: boolean): void {
  useEffect(() => {
    if (opts.focusTerms.current && opts.optionsOpen && inputPhase) {
      opts.focusTerms.current = false;
      opts.termsInput.current?.focus();
    }
  });
}

/** The input form's state and the actions that start, repeat or reset a run. */
export function useCleanTextForm(run: ReturnType<typeof useCleanTextRun>) {
  const [tab, setTab] = useState<'file' | 'url'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const opts = useOptionsState();
  const { language, extraTerms, keepTerms, setLanguage } = opts;
  const [localError, setLocalError] = useState<RunError | null>(null);
  const [rerun, setRerun] = useState(false);
  useFocusTerms(opts, run.phase === 'input');

  const options: RunOptions = { language, extraTerms, keepTerms };
  const source = sourceOf(tab, file, url);

  const clearErrors = (): void => {
    setLocalError(null);
    run.setError(null);
  };
  const choose = (picked: File | undefined): void => {
    if (!picked) return;
    run.setError(null);
    setFile(picked.size > MAX_FILE_BYTES ? null : picked);
    setLocalError(picked.size > MAX_FILE_BYTES ? tooLarge(picked) : null);
  };
  const submit = (): void => {
    clearErrors();
    if (tab === 'url' && !isHttpsAddress(url)) {
      setLocalError({ where: 'url', key: 'urlInvalid' });
      return;
    }
    if (source) void run.start(source, options);
  };
  const reprocessAs = (lang: 'it' | 'en'): void => {
    setLanguage(lang);
    if (source) void run.start(source, { ...options, language: lang });
  };
  const addTermAndRerun = (): void => {
    opts.focusTerms.current = true;
    opts.setOptionsOpen(true);
    setRerun(true);
    run.backToInput();
  };
  const newDocument = (): void => {
    setFile(null);
    setUrl('');
    setRerun(false);
    run.backToInput();
  };

  return {
    tab,
    file,
    url,
    rerun,
    error: localError ?? run.error,
    sourceName: tab === 'file' ? (file?.name ?? '') : safeHost(url.trim()),
    options: opts.options,
    onTab: (id: 'file' | 'url') => {
      setTab(id);
      clearErrors();
    },
    onUrl: (value: string) => {
      setUrl(value);
      clearErrors();
    },
    choose,
    submit,
    reprocessAs,
    addTermAndRerun,
    newDocument,
  };
}
